import type { z } from "zod";

/**
 * fetch sarmalayici — sozlesme (docs/faz5-arayuz-spec.md §1):
 *  - `credentials: 'include'` (cerez oturumu)
 *  - gomulu modda bearer: `setBearerToken()` ile ayarlanan token varsa
 *    `Authorization: Bearer <token>` eklenir
 *  - 401 → varsayilan olarak /giris'e yonlendirir (login uc noktasinin
 *    KENDISI icin `noRedirect: true` gecilmeli — yanlis sifre bir form
 *    hatasidir, oturum sonu degil)
 *  - `require_tenant` 409 `{detail:'tenant_secilmedi'}` doner → /tenant-sec
 *  - Basarili yanit zod semasiyla dogrulanir; uyusmazlik ApiError firlatir.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

type Handler = () => void;

function defaultUnauthorizedHandler(): void {
  if (typeof window === "undefined") return;
  const base = normalizeBase(import.meta.env.BASE_URL);
  window.location.assign(`${base}giris`);
}

function defaultTenantRequiredHandler(): void {
  if (typeof window === "undefined") return;
  const base = normalizeBase(import.meta.env.BASE_URL);
  window.location.assign(`${base}tenant-sec`);
}

function normalizeBase(base: string): string {
  if (!base) return "/";
  return base.endsWith("/") ? base : `${base}/`;
}

let unauthorizedHandler: Handler = defaultUnauthorizedHandler;
let tenantRequiredHandler: Handler = defaultTenantRequiredHandler;
let bearerToken: string | null = null;

/** Test/embed ortaminda 401 davranisini degistirmek icin (varsayilan: /giris'e git). */
export function setUnauthorizedHandler(fn: Handler): void {
  unauthorizedHandler = fn;
}

/** Test/embed ortaminda 409 tenant_secilmedi davranisini degistirmek icin. */
export function setTenantRequiredHandler(fn: Handler): void {
  tenantRequiredHandler = fn;
}

/** Gomulu mod (ör. /embed/ikas): oturum cerezi yerine bearer token. */
export function setBearerToken(token: string | null): void {
  bearerToken = token;
}

export function getBearerToken(): string | null {
  return bearerToken;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** true ise 401/409 otomatik yonlendirme TETIKLENMEZ (ör. login/register formu). */
  noRedirect?: boolean;
  // `object` (index-imzasiz interface'ler dahil her nesneyi kabul eder) —
  // Record<string, unknown> somut arayuzlerin (RegisterPayload vb.) atanmasini
  // reddeder cunku bunlarin index imzasi yok.
  body?: BodyInit | object | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function safeJson(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function detailOf(body: unknown): string | undefined {
  if (isRecord(body) && typeof body.detail === "string") return body.detail;
  return undefined;
}

export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { noRedirect, headers, body, ...rest } = options;

  const finalHeaders = new Headers(headers);
  finalHeaders.set("Accept", "application/json");
  if (bearerToken) finalHeaders.set("Authorization", `Bearer ${bearerToken}`);

  let finalBody: BodyInit | null | undefined;
  if (body != null && !(body instanceof Blob) && !(body instanceof FormData) && !(body instanceof URLSearchParams) && typeof body !== "string") {
    finalBody = JSON.stringify(body);
    if (!finalHeaders.has("Content-Type")) finalHeaders.set("Content-Type", "application/json");
  } else {
    finalBody = body as BodyInit | null | undefined;
  }

  const res = await fetch(path, {
    credentials: "include",
    ...rest,
    headers: finalHeaders,
    body: finalBody,
  });

  const responseBody = await safeJson(res);

  if (res.status === 401) {
    if (!noRedirect) unauthorizedHandler();
    throw new ApiError(detailOf(responseBody) ?? "unauthorized", 401, responseBody);
  }

  if (res.status === 409 && detailOf(responseBody) === "tenant_secilmedi") {
    if (!noRedirect) tenantRequiredHandler();
    throw new ApiError("tenant_secilmedi", 409, responseBody);
  }

  if (!res.ok) {
    throw new ApiError(detailOf(responseBody) ?? `${path} → ${res.status}`, res.status, responseBody);
  }

  const parsed = schema.safeParse(responseBody);
  if (!parsed.success) {
    throw new ApiError("gecersiz_yanit_semasi", res.status, parsed.error.flatten());
  }
  return parsed.data;
}

export function apiGet<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<T> {
  return apiRequest(path, schema, { ...options, method: "GET" });
}

export function apiPost<T>(
  path: string,
  schema: z.ZodType<T>,
  payload?: object,
  options?: RequestOptions,
): Promise<T> {
  return apiRequest(path, schema, { ...options, method: "POST", body: payload });
}

export function apiPut<T>(
  path: string,
  schema: z.ZodType<T>,
  payload?: object,
  options?: RequestOptions,
): Promise<T> {
  return apiRequest(path, schema, { ...options, method: "PUT", body: payload });
}

export function apiPatch<T>(
  path: string,
  schema: z.ZodType<T>,
  payload?: object,
  options?: RequestOptions,
): Promise<T> {
  return apiRequest(path, schema, { ...options, method: "PATCH", body: payload });
}

export function apiDelete<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<T> {
  return apiRequest(path, schema, { ...options, method: "DELETE" });
}
